// /account — minimal settings surface. Read-only summary of the user
// + bond, plus a Sign Out button. Foundation for future expansions
// (notification opt-ins, data export, account deletion). Today the
// scope is intentionally bare: a place that exists so future
// features have a slot to land in.
//
// What this page intentionally does NOT do:
//   - Edit ember name (lore-locked at Awakening)
//   - Change bond.locale (locked per spec)
//   - Account deletion (defer until volume justifies the safety UX)
//   - Notification preferences (gated by email digest decision)
//
// Edge cases:
//   - Pre-Bond user (logged in but no bond yet): omit the "Your Bond"
//     section. Show only the account meta + Sign Out.
//   - Session email is null (rare but typed): render "—".

import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { performSignOut } from '../lib/auth-actions';
import { getSessionSafe } from '../lib/auth-client';
import { AwakeningCardActions } from '../components/AwakeningCardActions';
import { deleteMyAccount, getMyBond } from '../lib/api';
import { redirectIfLandingOnly } from '../lib/landing-mode';
import { relativeTime } from '../lib/letter-format';

export const Route = createFileRoute('/account')({
  beforeLoad: async () => {
    redirectIfLandingOnly();
    const data = await getSessionSafe();
    if (!data?.session) throw redirect({ to: '/login' });
    return { email: data.user?.email ?? null };
  },
  loader: async () => {
    const bond = await getMyBond();
    return { bond };
  },
  component: AccountPage,
});

function AccountPage() {
  const { t } = useTranslation();
  const { email } = Route.useRouteContext();
  const { bond } = Route.useLoaderData();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);

  async function onSignOut() {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await performSignOut();
      await navigate({ to: '/login' });
    } catch (err) {
      // Auth client errors are rare; surface as a no-op + revert state.
      // The session most likely remained server-side; user can retry.
      console.error('signOut failed', err);
      setSigningOut(false);
    }
  }

  const languageLabel = bond ? t(`account.languageNames.${bond.locale}`) : null;

  return (
    <main className="mx-auto flex min-h-[80vh] max-w-2xl flex-col items-center pt-12 text-center animate-fadeUp sm:pt-8 lg:pt-4">
      <p className="mt-12 font-display text-[0.62rem] text-accent-strong uppercase tracking-[0.5em] sm:text-xs lg:mt-8">
        {t('account.eyebrow')}
      </p>
      <h1 className="mt-5 max-w-2xl font-display font-light text-ash-100 text-3xl leading-snug sm:text-4xl">
        {t('account.heading')}
      </h1>
      <p className="mt-6 max-w-xl font-body text-ash-300 italic leading-relaxed sm:text-lg">
        {t('account.subtitle')}
      </p>

      <div className="mt-12 flex w-full max-w-xl flex-col gap-10 text-left">
        <Section title={t('account.sections.you')}>
          <Field label={t('account.fields.email')} value={email ?? '—'} />
        </Section>

        {bond && (
          <Section title={t('account.sections.yourBond')}>
            <Field label={t('account.fields.emberName')} value={bond.emberName} />
            <Field
              label={t('account.fields.awakened')}
              value={t('account.fields.awakenedAgo', { when: relativeTime(bond.bondStartedAt) })}
            />
            {languageLabel && (
              <Field
                label={t('account.fields.language')}
                value={languageLabel}
                hint={t('account.fields.locked')}
              />
            )}
          </Section>
        )}

        {bond?.awakeningCardSquareUrl && (
          <section>
            <h2 className="font-display text-[0.62rem] text-ash-400 uppercase tracking-[0.4em] sm:text-xs">
              {t('account.sections.awakeningCard')}
            </h2>
            <AwakeningCardActions bond={bond} />
          </section>
        )}
      </div>

      <button
        type="button"
        onClick={onSignOut}
        disabled={signingOut}
        className="mt-14 rounded-sm border border-accent/50 bg-paper-900/40 px-7 py-3 font-display text-[0.7rem] text-ash-200 uppercase tracking-[0.4em] transition hover:border-accent-strong/80 hover:bg-paper-800/50 hover:text-ash-50 disabled:cursor-not-allowed disabled:opacity-40 sm:text-xs"
      >
        {signingOut ? '…' : t('account.logout')}
      </button>

      <p className="mt-10 max-w-md font-body text-ash-500 text-xs italic leading-relaxed">
        {t('account.closingNote')}
      </p>

      <a
        href="https://instagram.com/athrelaris"
        target="_blank"
        rel="noreferrer"
        className="mt-6 font-display text-[0.62rem] text-ash-500 uppercase tracking-[0.4em] transition hover:text-ash-300"
      >
        @athrelaris
      </a>

      {email && (
        <div className="mt-20 w-full max-w-xl border-paper-800/40 border-t pt-10">
          <DeleteAccountAction email={email} />
        </div>
      )}
    </main>
  );
}

function DeleteAccountAction({ email }: { email: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirmed = typed.trim().toLowerCase() === email.toLowerCase();

  async function onDelete() {
    if (!confirmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      await deleteMyAccount(typed);
      // Session is already gone server-side via the user delete cascade,
      // but clear local cache + signOut to be defensive.
      await performSignOut();
      await navigate({ to: '/' });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <div className="text-center">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="font-display text-[0.62rem] text-accent-strong uppercase tracking-[0.34em] transition hover:text-ash-200"
        >
          {t('account.delete.cta')}
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-sm border border-accent-strong/40 bg-paper-900/50 p-5 text-left sm:p-6">
      <p className="font-display text-[0.62rem] text-accent-strong uppercase tracking-[0.34em]">
        {t('account.delete.heading')}
      </p>
      <p className="mt-3 font-body text-ash-300 text-sm leading-relaxed">
        {t('account.delete.body')}
      </p>
      <label className="mt-4 block font-display text-[0.58rem] text-ash-500 uppercase tracking-[0.32em]">
        {t('account.delete.confirmLabel')}
      </label>
      <input
        type="email"
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        disabled={busy}
        placeholder={email}
        autoComplete="off"
        spellCheck={false}
        className="mt-2 w-full max-w-sm rounded-sm border border-paper-800/60 bg-paper-950/60 px-3 py-2 font-body text-ash-100 text-sm placeholder:text-ash-600 focus:border-accent-strong/60 focus:outline-none"
        autoFocus
      />
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onDelete}
          disabled={!confirmed || busy}
          className="rounded-sm border border-accent-strong bg-accent-strong/20 px-4 py-2 font-display text-[0.62rem] text-accent-strong uppercase tracking-[0.34em] transition hover:bg-accent-strong/30 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {busy ? '…' : t('account.delete.confirmButton')}
        </button>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setTyped('');
            setError(null);
          }}
          disabled={busy}
          className="font-display text-[0.62rem] text-ash-400 uppercase tracking-[0.34em] transition hover:text-ash-200 disabled:opacity-40"
        >
          {t('account.delete.cancel')}
        </button>
        {error && (
          <span className="font-body text-accent-strong text-xs italic">{error}</span>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="font-display text-[0.62rem] text-ash-400 uppercase tracking-[0.4em] sm:text-xs">
        {title}
      </h2>
      <dl className="mt-4 space-y-3">{children}</dl>
    </section>
  );
}

function Field({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div>
      <dt className="font-display text-[0.58rem] text-ash-500 uppercase tracking-[0.34em]">
        {label}
      </dt>
      <dd className="mt-1 font-body text-ash-100 leading-relaxed sm:text-[1.02rem]">
        {value}
        {hint && (
          <span className="ml-2 font-body text-ash-500 text-xs italic">{hint}</span>
        )}
      </dd>
    </div>
  );
}
